import macro from '@kitware/vtk.js/macros';
import vtkCamera from '@kitware/vtk.js/Rendering/Core/Camera';
import { getProjectionScaleMatrix } from '../helpers/getProjectionScaleMatrix';
import { getNormalizedAspectRatio } from '../../utilities/getNormalizedAspectRatio';
import { mat4 } from 'gl-matrix';

interface ICameraInitialValues {
  position?: number[];
  focalPoint?: number[];
  viewUp?: number[];
  directionOfProjection?: number[];
  parallelProjection?: boolean;
  useHorizontalViewAngle?: boolean;
  viewAngle?: number;
  parallelScale?: number;
  clippingRange?: number[];
  windowCenter?: number[];
  viewPlaneNormal?: number[];
  useOffAxisProjection?: boolean;
  screenBottomLeft?: number[];
  screenBottomRight?: number[];
  screenTopRight?: number[];
  freezeFocalPoint?: boolean;
  physicalTranslation?: number[];
  physicalScale?: number;
  physicalViewUp?: number[];
  physicalViewNorth?: number[];
  aspectRatio?: number[];
}

declare module '@kitware/vtk.js/Rendering/Core/Camera' {
  export interface vtkCamera {
    /**
     * Get the aspectRatio of the viewport
     *  @defaultValue [1, 1]
     */
    getAspectRatio(): [x: number, y: number];

    /**
     * Sets the viewport aspect ratio used for canvas-space stretching.
     * The aspect ratio is applied along the canvas X and Y axes,
     * independent of the image orientation.
     *
     * @param aspectRatio - [scaleX, scaleY] stretch factors applied along the canvas X and Y axes
     */
    setAspectRatio(aspectRatio: [x: number, y: number]): boolean;
  }
}

export type extendedVtkCamera = vtkCamera;
/**
 * extendedVtkCamera - A derived class of the core vtkCamera class
 *
 * This customization is necessary because when need to handle stretched viewport
 *
 * @param {*} publicAPI The public API to extend
 * @param {*} model The private model to extend.
 */
function extendedVtkCamera(publicAPI, model) {
  model.classHierarchy.push('extendedVtkCamera');

  // Keep original
  const superGetProjectionMatrix = publicAPI.getProjectionMatrix;

  /**
   * getProjectionMatrix - A fork of vtkCamera's getProjectionMatrix method.
   * This fork performs most of the same actions, but added handling for stretched viewport.
   */
  publicAPI.getProjectionMatrix = (aspect, nearZ, farZ) => {
    const matrix = superGetProjectionMatrix(aspect, nearZ, farZ);

    const [sx, sy] = getNormalizedAspectRatio(model.aspectRatio);

    if (sx !== 1.0 || sy !== 1.0) {
      const scaleMatrix = getProjectionScaleMatrix([sx, sy]);
      mat4.multiply(matrix, scaleMatrix, matrix);
    }

    return matrix;
  };
}

// ----------------------------------------------------------------------------
// Object factory
// ----------------------------------------------------------------------------

// ----------------------------------------------------------------------------

const DEFAULT_VALUES = {
  aspectRatio: [1, 1],
};

export function extend(publicAPI, model, initialValues = {}) {
  Object.assign(model, DEFAULT_VALUES, initialValues);

  vtkCamera.extend(publicAPI, model, initialValues);

  // Register the aspect ratio set/get methods here.
  macro.setGet(publicAPI, model, ['aspectRatio']);

  // Object methods
  extendedVtkCamera(publicAPI, model);
}

// ----------------------------------------------------------------------------

export const newInstance: (
  initialValues?: ICameraInitialValues
) => extendedVtkCamera = macro.newInstance(extend, 'extendedVtkCamera');
// ----------------------------------------------------------------------------

export default { newInstance, extend };
